/**
 * Credential-guard decorators for the S3 client wrappers.
 *
 * Every public S3 method on these clients has to make sure its credentials are
 * still valid before it issues a command, which used to mean repeating
 * `await this.ensureCredentialsValid()` as the first line of each one. These
 * decorators hold that single line instead.
 *
 * For the VDR client these remain load-bearing even though its `S3Client` now
 * refreshes credentials on its own, per signed request: the bucket a command is
 * addressed to arrives *with* the credentials, and command inputs are built
 * before the SDK resolves them. Without this pre-warm, the first command of a
 * session would be addressed to the `ENV` fallback bucket. They also keep
 * failures loud — an auth error surfaces from `fileExists()` rather than from
 * deep inside the request signer.
 *
 * The trade-off, stated plainly: the guard is no longer visible in the method
 * body, so a method added later that forgets the decorator will silently run
 * on expired credentials. `tests/unit/utils/aws-clients/s3-vdr-client-test.ts`
 * exists to catch exactly that.
 */

/**
 * Structural type for the wrapper's `this`. Annotating it here is what makes
 * the decorators type-check against a class whose `ensureCredentialsValid` is
 * `private` — TypeScript never structurally matches a decorator against the
 * class it decorates, so no visibility change is needed on the clients.
 */
type CredentialedClient = {
  ensureCredentialsValid(): Promise<void>;
};

type AsyncMethod = (...args: never[]) => Promise<unknown>;
type AsyncGeneratorMethod = (...args: never[]) => AsyncGenerator<unknown>;

/**
 * Refreshes credentials if they are close to expiring, then runs the method.
 * For ordinary `async` methods only — see `requiresCredentialsStream` for
 * async generators.
 */
export function requiresCredentials(
  _target: object,
  _key: string | symbol,
  descriptor: PropertyDescriptor,
): PropertyDescriptor {
  const original = descriptor.value as AsyncMethod;

  descriptor.value = async function (
    this: CredentialedClient,
    ...args: never[]
  ): Promise<unknown> {
    await this.ensureCredentialsValid();
    return original.apply(this, args);
  };

  return descriptor;
}

/**
 * The async-generator counterpart, for methods that `yield` rather than
 * return — `downloadFileWithProgress` streams progress updates that way.
 *
 * These need their own decorator rather than a runtime check inside
 * `requiresCredentials`: telling an async generator function apart from a
 * plain async one at runtime (`constructor.name`, `Symbol.toStringTag`) is not
 * dependable through Babel's transform chain, and getting it wrong returns a
 * promise where callers `for await`. Two explicitly named decorators cannot
 * fail that way.
 *
 * Note the guard runs on the first `next()`, not when the method is called —
 * generator bodies are lazy, and wrapping does not change that.
 */
export function requiresCredentialsStream(
  _target: object,
  _key: string | symbol,
  descriptor: PropertyDescriptor,
): PropertyDescriptor {
  const original = descriptor.value as AsyncGeneratorMethod;

  descriptor.value = async function* (
    this: CredentialedClient,
    ...args: never[]
  ): AsyncGenerator<unknown> {
    await this.ensureCredentialsValid();
    yield* original.apply(this, args);
  };

  return descriptor;
}
